import React, { useState } from "react";
import { useDispatch } from "react-redux";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Divider from "@mui/material/Divider";
import Resizer from "react-image-file-resizer";
import { startLoading, stopLoading } from "store/actions";
import { submitOnboarding } from "../../actions/dataLoad";
import CloudUploadIcon from '@material-ui/icons/CloudUpload';

const primaryColor = "#379E90";
const secondaryColor = "#7e858e";

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: '85%', md: '550px' },
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: '#FFFCF8', // Match app body background
  boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
  p: 0,
  borderRadius: '20px',
  outline: 'none',
  fontFamily: '"Quicksand", sans-serif',
};

const TherapistOnboardingModal = ({ show, status, userToken, userInfo, onComplete, onLogout }) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const [fullName, setFullName] = useState(status?.currentValues?.fullName ?? "");
  const [bio, setBio] = useState(status?.currentValues?.bio ?? "");
  const [expInY, setExpInY] = useState(status?.currentValues?.experienceInYears ?? "");
  const [minAge, setMinAge] = useState(status?.currentValues?.allowedAgeMin ?? "");
  const [maxAge, setMaxAge] = useState(status?.currentValues?.allowedAgeMax ?? "");
  const [sessionPerWeek, setSessionPerWeek] = useState(status?.currentValues?.sessionsPerWeek ?? "");
  const [therapistType, setTherapistType] = useState("1");
  const [therapistImage, setTherapistImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedProblems, setSelectedProblems] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [error, setError] = useState("");

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImagePreview(URL.createObjectURL(file));
      Resizer.imageFileResizer(
        file,
        720,
        720,
        "JPEG",
        100,
        0,
        (uri) => {
          setTherapistImage(uri);
        },
        "base64"
      );
    }
  };

  const validateStep = () => {
    setError("");
    if (step === 1) {
      if (
        fullName === "" || fullName === null ||
        bio === "" || bio === null ||
        expInY === "" || expInY === null ||
        minAge === "" || minAge === null ||
        maxAge === "" || maxAge === null ||
        sessionPerWeek === "" || sessionPerWeek === null
      ) {
        setError("All fields are required to continue.");
        return false;
      }
      if (Number(minAge) > Number(maxAge)) {
        setError("Minimum age cannot be greater than maximum age.");
        return false;
      }
      if (Number(expInY) < 0 || Number(minAge) < 0 || Number(maxAge) < 0 || Number(sessionPerWeek) < 0) {
        setError("Values cannot be negative.");
        return false;
      }
    } else if (step === 2) {
      if (selectedProblems.length === 0 || selectedTypes.length === 0) {
        setError("Please select at least one specialization.");
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!therapistImage) {
      setError("Please upload a profile picture.");
      return;
    }

    const data = {
      fullName,
      bio,
      experienceInYears: Number(expInY),
      allowedAgeMin: Number(minAge),
      allowedAgeMax: Number(maxAge),
      sessionsPerWeek: Number(sessionPerWeek),
      problemIds: selectedProblems,
      therapyTypes: selectedTypes,
      therapistPriority: therapistType,
      image: therapistImage,
    };

    try {
      dispatch(startLoading());
      await submitOnboarding(data, userToken);
      dispatch(stopLoading());
      onComplete();
    } catch (err) {
      dispatch(stopLoading());
      setError(err.response?.data?.error || "Failed to save. Please try again.");
    }
  };

  const renderStepIndicator = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mb: 4 }}>
      {[1, 2, 3].map((s) => (
        <Box
          key={s}
          sx={{
            width: s === step ? 24 : 10,
            height: 10,
            borderRadius: 5,
            bgcolor: s === step ? primaryColor : '#E0E0E0',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
      ))}
    </Box>
  );

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      fontFamily: '"Quicksand", sans-serif',
      '& fieldset': { borderColor: '#E0E0E0' },
      '&:hover fieldset': { borderColor: primaryColor },
      '&.Mui-focused fieldset': { borderColor: primaryColor },
    },
    '& .MuiInputLabel-root': { fontFamily: '"Quicksand", sans-serif' },
    '& .MuiInputLabel-root.Mui-focused': { color: primaryColor },
    '& .MuiInputBase-input': { fontFamily: '"Quicksand", sans-serif' },
  };

  const checkboxSx = {
    color: '#DDD', 
    '&.Mui-checked': { color: primaryColor }
  };

  return (
    <Modal open={show} onClose={() => {}} disableEscapeKeyDown>
      <Box sx={modalStyle}>
        {/* Header Section */}
        <Box sx={{ p: 4, pt: 5, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#2C2C2C', fontFamily: '"Quicksand", sans-serif' }}>
            {step === 1 ? "Professional Profile" : step === 2 ? "Your Specializations" : "Complete Registration"}
          </Typography>
          <Typography variant="body2" sx={{ color: secondaryColor, mt: 1, fontFamily: '"Quicksand", sans-serif' }}>
            Fill in your details to begin your journey with GoodLives
          </Typography>
        </Box>

        <Box sx={{ px: { xs: 3, md: 6 }, pb: 5 }}>
          {renderStepIndicator()}
          
          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '8px', fontSize: '0.875rem', fontFamily: '"Quicksand", sans-serif' }}>{error}</Alert>}

          <form id="onboarding-form" onSubmit={handleSubmit} style={{ fontFamily: '"Quicksand", sans-serif' }}>
            {step === 1 && (
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <TextField 
                    fullWidth required type="text" label="Full Name" 
                    value={fullName} onChange={(e) => setFullName(e.target.value)} 
                    variant="outlined" sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    fullWidth required type="text" label="Short Bio" multiline rows={3}
                    value={bio} onChange={(e) => setBio(e.target.value)} 
                    variant="outlined" sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    fullWidth required type="number" label="Years of Experience" 
                    value={expInY} onChange={(e) => setExpInY(e.target.value)} 
                    variant="outlined" sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField 
                    fullWidth required type="number" label="Min Client Age" 
                    value={minAge} onChange={(e) => setMinAge(e.target.value)} 
                    variant="outlined" sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField 
                    fullWidth required type="number" label="Max Client Age" 
                    value={maxAge} onChange={(e) => setMaxAge(e.target.value)} 
                    variant="outlined" sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    fullWidth required type="number" label="Available Sessions per Week" 
                    value={sessionPerWeek} onChange={(e) => setSessionPerWeek(e.target.value)} 
                    variant="outlined" sx={inputSx}
                  />
                </Grid>
              </Grid>
            )}

            {step === 2 && (
              <Box sx={{ mt: -1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.5, color: '#444', fontFamily: '"Quicksand", sans-serif' }}>Problem Categories</Typography>
                <Paper elevation={0} sx={{ p: 2, mb: 3, maxHeight: 180, overflowY: 'auto', borderRadius: '10px', border: '1px solid #EEE', bgcolor: '#FFFCF8' }}>
                  <Grid container spacing={0.5}>
                    {status.problems.map((p) => (
                      <Grid item xs={12} sm={6} key={p._id}>
                        <FormControlLabel
                          control={
                            <Checkbox 
                              name={p._id} 
                              size="small" 
                              sx={checkboxSx} 
                              checked={selectedProblems.includes(p.problemID)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedProblems([...selectedProblems, p.problemID]);
                                } else {
                                  setSelectedProblems(selectedProblems.filter(id => id !== p.problemID));
                                }
                              }}
                            />
                          }
                          label={<Typography sx={{ fontSize: '0.9rem', fontFamily: '"Quicksand", sans-serif' }}>{p.problemCategory}</Typography>}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>

                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.5, color: '#444', fontFamily: '"Quicksand", sans-serif' }}>Therapy Approaches</Typography>
                <Paper elevation={0} sx={{ p: 2, maxHeight: 180, overflowY: 'auto', borderRadius: '10px', border: '1px solid #EEE', bgcolor: '#FFFCF8' }}>
                  <Grid container spacing={0.5}>
                    {status.types.map((t) => (
                      <Grid item xs={12} sm={6} key={t._id}>
                        <FormControlLabel
                          control={
                            <Checkbox 
                              name={t._id} 
                              size="small" 
                              sx={checkboxSx} 
                              checked={selectedTypes.includes(t.therapyType)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedTypes([...selectedTypes, t.therapyType]);
                                } else {
                                  setSelectedTypes(selectedTypes.filter(id => id !== t.therapyType));
                                }
                              }}
                            />
                          }
                          label={<Typography sx={{ fontSize: '0.9rem', fontFamily: '"Quicksand", sans-serif' }}>{t.therapy}</Typography>}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Box>
            )}

            {step === 3 && (
              <Box sx={{ textAlign: 'center' }}>
                <Box 
                  sx={{ 
                    width: 120, height: 120, borderRadius: '50%', bgcolor: '#F8F9FA', 
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', border: `2px dashed ${primaryColor}`,
                    mx: 'auto', mb: 2, cursor: 'pointer', position: 'relative',
                    transition: '0.2s', '&:hover': { bgcolor: '#F0F2F5' }
                  }}
                  component="label"
                >
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <CloudUploadIcon style={{ fontSize: 32, color: primaryColor, opacity: 0.6 }} />
                  )}
                  <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                </Box>
                <Typography variant="caption" sx={{ color: secondaryColor, fontFamily: '"Quicksand", sans-serif' }}>Upload profile photo</Typography>

              </Box>
            )}

            <Box sx={{ mt: 5, display: 'flex', gap: 2 }}>
              {step > 1 && (
                <Button fullWidth onClick={handleBack} variant="outlined" sx={{ 
                  borderRadius: '8px', textTransform: 'none', fontWeight: 600, py: 1.2, fontFamily: '"Quicksand", sans-serif',
                  borderColor: '#EEE', color: secondaryColor, '&:hover': { borderColor: '#DDD', bgcolor: '#F9F9F9' }
                }}>
                  Back
                </Button>
              )}

              <Button 
                fullWidth 
                type={step === 3 ? "submit" : "button"}
                onClick={step === 3 ? undefined : handleNext} 
                variant="contained" 
                disableElevation
                sx={{ 
                  bgcolor: primaryColor, borderRadius: '8px', textTransform: 'none', fontWeight: 600, py: 1.2, fontFamily: '"Quicksand", sans-serif',
                  '&:hover': { bgcolor: '#2D8B7E' }
                }}
              >
                {step === 3 ? "Complete Profile" : "Continue"}
              </Button>
            </Box>
          </form>
        </Box>
      </Box>
    </Modal>
  );
};

export default TherapistOnboardingModal;
